import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { DASHBOARD_CAROUSEL_ENTRIES } from "../config/dashboardCarousel";
import {
  deriveCarouselOrderFromWidgetOrder,
} from "../config/dashboardWidgets";
import { loadDashboardWidgetOrder } from "../helpers/dashboardWidgetOrder";
import { DashboardPreviewCard } from "./DashboardPreviewCard";
import { usePermissions } from "../hooks/usePermissions";
import { canAccessDashboardScreen } from "../helpers/screenGuards";

const CLONE_SETS = 3;
const SCROLL_END_DEBOUNCE_MS = 120;

function computeSlideVisuals(slideEl, containerEl) {
  const containerRect = containerEl.getBoundingClientRect();
  const slideRect = slideEl.getBoundingClientRect();

  const containerCenterY = containerRect.top + containerRect.height / 2;
  const slideCenterY = slideRect.top + slideRect.height / 2;
  const distance = Math.abs(slideCenterY - containerCenterY);
  const slideHeight = slideRect.height || 1;

  const ratio = Math.min(distance / (slideHeight * 0.75), 1);

  const scale = 1 - ratio * 0.14;
  const opacity = 1 - ratio * 0.48;
  const elevation = 1 - ratio;
  const shadowY = 4 + elevation * 14;
  const shadowBlur = 6 + elevation * 26;
  const shadowAlpha = 0.12 + elevation * 0.32;

  return {
    scale,
    opacity,
    elevation,
    boxShadow: `0 ${shadowY}px ${shadowBlur}px rgba(0, 0, 0, ${shadowAlpha})`,
    distance,
  };
}

export function DashboardNavigatorPicker({ currentScreen, onSelect }) {
  const { actor, orgPolicies } = usePermissions();
  const entries = useMemo(() => {
    const byId = Object.fromEntries(
      DASHBOARD_CAROUSEL_ENTRIES.map((entry) => [entry.id, entry]),
    );
    const order = deriveCarouselOrderFromWidgetOrder(
      loadDashboardWidgetOrder(actor),
    );
    return order
      .map((id) => byId[id])
      .filter(
        (entry) =>
          entry && canAccessDashboardScreen(actor, entry.id, orgPolicies),
      );
  }, [actor, orgPolicies]);
  const count = entries.length;
  const pickerRef = useRef(null);
  const slideRefs = useRef([]);
  const rafRef = useRef(null);
  const scrollEndTimerRef = useRef(null);
  const isRepositioningRef = useRef(false);

  const loopedEntries = useMemo(
    () => Array.from({ length: CLONE_SETS }, () => entries).flat(),
    [entries],
  );

  const currentIndex = useMemo(() => {
    const idx = entries.findIndex((entry) => entry.id === currentScreen);
    return idx === -1 ? 0 : idx;
  }, [entries, currentScreen]);

  const middleSetStart = count;
  const [selectedIndex, setSelectedIndex] = useState(middleSetStart + currentIndex);

  const getClosestVirtualIndex = useCallback(() => {
    const container = pickerRef.current;
    if (!container) {
      return middleSetStart + currentIndex;
    }

    const slides = slideRefs.current.filter(Boolean);
    let closestIndex = 0;
    let closestDistance = Infinity;

    slides.forEach((slide, index) => {
      const visuals = computeSlideVisuals(slide, container);

      if (visuals.distance < closestDistance) {
        closestDistance = visuals.distance;
        closestIndex = index;
      }
    });

    return closestIndex;
  }, [currentIndex, middleSetStart]);

  const updateSlideVisuals = useCallback(() => {
    const container = pickerRef.current;
    if (!container) {
      return;
    }

    const slides = slideRefs.current.filter(Boolean);
    if (!slides.length) {
      return;
    }

    let closestIndex = 0;
    let closestDistance = Infinity;

    slides.forEach((slide, index) => {
      const visuals = computeSlideVisuals(slide, container);

      slide.style.transform = `scale(${visuals.scale})`;
      slide.style.opacity = String(visuals.opacity);
      slide.style.boxShadow = visuals.boxShadow;
      slide.style.zIndex = String(Math.round(visuals.elevation * 10));

      if (visuals.distance < closestDistance) {
        closestDistance = visuals.distance;
        closestIndex = index;
      }
    });

    slides.forEach((slide, index) => {
      slide.classList.toggle(
        "dashboard-navigator-picker-slide--selected",
        index === closestIndex,
      );
      slide.setAttribute(
        "aria-selected",
        index === closestIndex ? "true" : "false",
      );
    });

    setSelectedIndex((prev) => (prev === closestIndex ? prev : closestIndex));
  }, []);

  const scrollToVirtualIndex = useCallback((index, smooth = true) => {
    const slide = slideRefs.current[index];
    if (!slide) {
      return;
    }

    isRepositioningRef.current = !smooth;

    slide.scrollIntoView({
      behavior: smooth ? "smooth" : "instant",
      block: "center",
      inline: "nearest",
    });

    if (!smooth) {
      requestAnimationFrame(() => {
        isRepositioningRef.current = false;
        updateSlideVisuals();
      });
    }
  }, [updateSlideVisuals]);

  const normalizeLoopPosition = useCallback(() => {
    if (isRepositioningRef.current) {
      return;
    }

    const closestIndex = getClosestVirtualIndex();

    if (closestIndex < count) {
      scrollToVirtualIndex(closestIndex + count, false);
      return;
    }

    if (closestIndex >= count * 2) {
      scrollToVirtualIndex(closestIndex - count, false);
    }
  }, [count, getClosestVirtualIndex, scrollToVirtualIndex]);

  useEffect(() => {
    let frameOne = 0;
    let frameTwo = 0;

    frameOne = requestAnimationFrame(() => {
      frameTwo = requestAnimationFrame(() => {
        scrollToVirtualIndex(middleSetStart + currentIndex, false);
        updateSlideVisuals();
      });
    });

    return () => {
      cancelAnimationFrame(frameOne);
      cancelAnimationFrame(frameTwo);
    };
  }, [currentIndex, middleSetStart, scrollToVirtualIndex, updateSlideVisuals]);

  useEffect(() => {
    const container = pickerRef.current;
    if (!container) {
      return undefined;
    }

    const handleScroll = () => {
      if (isRepositioningRef.current) {
        return;
      }

      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      rafRef.current = requestAnimationFrame(updateSlideVisuals);

      if (scrollEndTimerRef.current) {
        clearTimeout(scrollEndTimerRef.current);
      }

      scrollEndTimerRef.current = setTimeout(() => {
        normalizeLoopPosition();
      }, SCROLL_END_DEBOUNCE_MS);
    };

    const handleScrollEnd = () => {
      normalizeLoopPosition();
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    container.addEventListener("scrollend", handleScrollEnd);
    window.addEventListener("resize", updateSlideVisuals);

    updateSlideVisuals();

    return () => {
      container.removeEventListener("scroll", handleScroll);
      container.removeEventListener("scrollend", handleScrollEnd);
      window.removeEventListener("resize", updateSlideVisuals);

      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      if (scrollEndTimerRef.current) {
        clearTimeout(scrollEndTimerRef.current);
      }
    };
  }, [normalizeLoopPosition, updateSlideVisuals]);

  return (
    <div
      ref={pickerRef}
      className="dashboard-navigator-picker"
      role="listbox"
      aria-label="Dashboard selector"
    >
      <ul className="dashboard-navigator-picker-track">
        {loopedEntries.map((entry, virtualIndex) => (
          <li
            key={`${entry.id}-${virtualIndex}`}
            data-virtual-index={virtualIndex}
            ref={(el) => {
              slideRefs.current[virtualIndex] = el;
            }}
            className={`dashboard-navigator-picker-slide${
              selectedIndex === virtualIndex
                ? " dashboard-navigator-picker-slide--selected"
                : ""
            }`}
            role="option"
            aria-selected={selectedIndex === virtualIndex}
          >
            <DashboardPreviewCard
              entry={entry}
              isCurrent={entry.id === currentScreen}
              onSelect={onSelect}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}
